import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useStripeTerminalSession } from '@/hooks/useStripeTerminalSession';
import { useTerminalConfiguration } from '@/hooks/useRevenueData';
import { createTerminalConnectionToken } from '@/services/revenue/functions';
import type { TerminalConfigurationResponse } from '@/services/revenue/functions';
import {
  StripeTerminalContext,
  type StripeTerminalContextValue,
} from '@/context/stripe-terminal-context';
import { appToast } from '@/lib/appToast';
import {
  getLastConnectedReaderId,
  getTerminalReconnectDelayMs,
  MAX_TERMINAL_RECONNECT_ATTEMPTS,
  pickTerminalReader,
  saveLastConnectedReaderId,
  TERMINAL_RECONNECT_FAILED_MESSAGE,
  TERMINAL_UNEXPECTED_DISCONNECT_MESSAGE,
} from '@/utils/terminalReconnect';

function resolveDiscoveryConfig(terminalConfig: TerminalConfigurationResponse | undefined) {
  const stripeLocationId = terminalConfig?.stripeLocationId?.trim();

  if (!stripeLocationId) {
    throw new Error(
      'Stripe Terminal location is not configured. Add a Stripe location ID in Settings.',
    );
  }

  return { stripeLocationId };
}

export function StripeTerminalProvider({ children }: PropsWithChildren) {
  const terminalConfigQuery = useTerminalConfiguration();
  const terminalConfig = terminalConfigQuery.data;
  const autoConnectAttemptedRef = useRef(false);
  const manuallyDisconnectedRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectInFlightRef = useRef(false);
  const unexpectedDisconnectToastShownRef = useRef(false);

  const connectionTokenMutation = useMutation({
    mutationFn: () => createTerminalConnectionToken(),
  });

  const fetchConnectionToken = useCallback(
    () => connectionTokenMutation.mutateAsync(),
    [connectionTokenMutation],
  );

  const terminalSession = useStripeTerminalSession(fetchConnectionToken);
  const {
    connectedReader,
    connectReader,
    discoverReaders,
    disconnectReader,
    registerUnexpectedDisconnectHandler,
    ...terminalState
  } = terminalSession;
  const connectedReaderRef = useRef(connectedReader);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [reconnectFailed, setReconnectFailed] = useState(false);

  useEffect(() => {
    connectedReaderRef.current = connectedReader;
  }, [connectedReader]);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const connectDefaultReader = useCallback(
    async (options?: { resetAttempts?: boolean }) => {
      if (connectedReader) {
        return connectedReader;
      }

      manuallyDisconnectedRef.current = false;

      if (options?.resetAttempts) {
        reconnectAttemptRef.current = 0;
        setReconnectFailed(false);
        unexpectedDisconnectToastShownRef.current = false;
      }

      setIsConnecting(true);

      try {
        const discoveryConfig = resolveDiscoveryConfig(terminalConfig);
        const discoveredReaders = await discoverReaders(discoveryConfig);
        const readerToConnect = pickTerminalReader(
          discoveredReaders,
          terminalConfig,
          getLastConnectedReaderId(),
        );

        if (!readerToConnect) {
          throw new Error('No card readers found. Check your Terminal configuration.');
        }

        const connected = await connectReader(readerToConnect);
        saveLastConnectedReaderId(connected.id);
        reconnectAttemptRef.current = 0;
        setReconnectFailed(false);
        unexpectedDisconnectToastShownRef.current = false;
        return connected;
      } finally {
        setIsConnecting(false);
      }
    },
    [connectReader, connectedReader, discoverReaders, terminalConfig],
  );

  const disconnectDefaultReader = useCallback(async () => {
    manuallyDisconnectedRef.current = true;
    clearReconnectTimeout();
    reconnectInFlightRef.current = false;
    setIsConnecting(false);
    setIsReconnecting(false);
    setReconnectFailed(false);
    reconnectAttemptRef.current = 0;
    await disconnectReader();
  }, [clearReconnectTimeout, disconnectReader]);

  const scheduleReconnect = useCallback(
    (reason: 'unexpected_disconnect' | 'retry') => {
      if (
        manuallyDisconnectedRef.current ||
        connectedReaderRef.current ||
        reconnectInFlightRef.current ||
        isConnecting
      ) {
        return;
      }

      if (reconnectAttemptRef.current >= MAX_TERMINAL_RECONNECT_ATTEMPTS) {
        setReconnectFailed(true);
        setIsReconnecting(false);
        return;
      }

      const attempt = reconnectAttemptRef.current;
      const delayMs =
        reason === 'unexpected_disconnect' && attempt === 0
          ? 0
          : getTerminalReconnectDelayMs(attempt);

      clearReconnectTimeout();
      reconnectTimeoutRef.current = window.setTimeout(() => {
        reconnectTimeoutRef.current = null;

        if (
          connectedReaderRef.current ||
          reconnectInFlightRef.current ||
          isConnecting
        ) {
          return;
        }

        reconnectInFlightRef.current = true;
        setIsReconnecting(true);
        setReconnectFailed(false);
        reconnectAttemptRef.current += 1;

        void connectDefaultReader()
          .then((reader) => {
            if (reader) {
              appToast.success(`Reconnected to ${reader.label}.`);
            }
          })
          .catch(() => {
            if (reconnectAttemptRef.current >= MAX_TERMINAL_RECONNECT_ATTEMPTS) {
              setReconnectFailed(true);
              appToast.error(TERMINAL_RECONNECT_FAILED_MESSAGE);
              return;
            }

            scheduleReconnect('retry');
          })
          .finally(() => {
            reconnectInFlightRef.current = false;
            setIsReconnecting(false);
          });
      }, delayMs);
    },
    [clearReconnectTimeout, connectDefaultReader, isConnecting],
  );

  const scheduleReconnectRef = useRef(scheduleReconnect);
  scheduleReconnectRef.current = scheduleReconnect;

  useEffect(() => {
    registerUnexpectedDisconnectHandler(() => {
      if (!unexpectedDisconnectToastShownRef.current) {
        unexpectedDisconnectToastShownRef.current = true;
        appToast.info(TERMINAL_UNEXPECTED_DISCONNECT_MESSAGE);
      }

      scheduleReconnectRef.current('unexpected_disconnect');
    });

    return () => {
      registerUnexpectedDisconnectHandler(null);
    };
  }, [registerUnexpectedDisconnectHandler]);

  useEffect(() => {
    if (terminalConfigQuery.isLoading || autoConnectAttemptedRef.current) {
      return;
    }

    autoConnectAttemptedRef.current = true;

    if (connectedReader) {
      return;
    }

    void connectDefaultReader().catch(() => {
      // Staff can reconnect manually from the header control.
    });
  }, [connectedReader, connectDefaultReader, terminalConfigQuery.isLoading]);

  useEffect(() => () => clearReconnectTimeout(), [clearReconnectTimeout]);

  const value = useMemo<StripeTerminalContextValue>(
    () => ({
      ...terminalState,
      connectedReader,
      connectReader,
      discoverReaders,
      registerUnexpectedDisconnectHandler,
      terminalConfig,
      isConfigLoading: terminalConfigQuery.isLoading,
      isConnecting,
      isReconnecting,
      reconnectFailed,
      connectDefaultReader,
      disconnectDefaultReader,
    }),
    [
      terminalState,
      connectedReader,
      connectReader,
      discoverReaders,
      registerUnexpectedDisconnectHandler,
      terminalConfig,
      terminalConfigQuery.isLoading,
      isConnecting,
      isReconnecting,
      reconnectFailed,
      connectDefaultReader,
      disconnectDefaultReader,
    ],
  );

  return (
    <StripeTerminalContext.Provider value={value}>{children}</StripeTerminalContext.Provider>
  );
}
